import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trophy, Crown, Medal, ArrowLeft, Sparkles, Clock } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatEuros } from "../lib/format";
import { toast } from "sonner";

export default function Leaderboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/challenges/${id}/leaderboard`);
        setData(data);
      } catch (e) {
        // 403 → not finalized yet AND user hasn't played: stay on the page
        // and show pending state.
        if (e?.response?.status === 403) {
          setData({ rows: [], status: "pending_finalization", pending: true });
          return;
        }
        toast.error(formatApiError(e));
        navigate("/");
      }
    })();
  }, [id, navigate]);

  if (!data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16 text-white/50 font-mono-stat uppercase">
          Chargement...
        </div>
      </Layout>
    );
  }

  const top3 = data.rows.slice(0, 3);
  const others = data.rows.slice(3);
  const order = [1, 0, 2]; // visual order: 2nd, 1st, 3rd
  const isProvisional = !!data.provisional;
  const isQualifier = data.reward_type === "qualification";
  const qualSlots = Number(data.qualification_slots || 0);
  const winnersCount = Number(data.winners_count || 0);
  const hasCashPool = Number(data.prize_pool_base || 0) > 0;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/")}
          data-testid="leaderboard-back-button"
          className="flex items-center gap-2 text-white/60 hover:text-cyan-300 mb-6 text-sm font-mono-stat uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="text-center mb-10">
          <div
            data-testid="leaderboard-title-tag"
            className={`text-xs uppercase tracking-[0.3em] font-mono-stat mb-2 ${
              isProvisional ? "text-cyan-300" : "text-fuchsia-300"
            }`}
          >
            {isProvisional ? "Classement provisoire" : "Classement final"}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
            Podium
          </h1>
        </div>

        {isProvisional && data.rows.length > 0 && (
          <div
            data-testid="leaderboard-provisional-banner"
            className="mb-8 rounded-2xl border border-cyan-400/50 bg-cyan-400/8 p-5 flex items-start gap-3"
          >
            <Clock className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-display font-bold uppercase tracking-wider text-cyan-200 mb-1">
                Le quiz est encore ouvert
              </div>
              <p className="text-white/70 leading-relaxed">
                Ce classement est <span className="text-cyan-200 font-bold">provisoire</span>{" "}
                et peut évoluer jusqu&apos;à la fermeture du quiz.{" "}
                {isQualifier
                  ? "Les qualifications ne seront confirmées qu'à la clôture."
                  : hasCashPool
                  ? "Les gains ne seront confirmés qu'à la clôture."
                  : "Les résultats seront figés à la clôture."}
              </p>
              {data.my_provisional_rank && (
                <p
                  data-testid="leaderboard-my-provisional-rank"
                  className="mt-2 text-white/85 font-mono-stat"
                >
                  Ta position actuelle :{" "}
                  <span className="text-cyan-200 font-bold">
                    #{data.my_provisional_rank}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        {data.pending || data.status === "pending_finalization" && data.rows.length === 0 ? (
          <div
            data-testid="leaderboard-pending"
            className="panel rounded-2xl p-10 text-center border border-orange-400/40 bg-orange-400/5"
          >
            <Trophy className="w-10 h-10 mx-auto text-orange-300 mb-3" />
            <h3 className="font-display text-xl font-bold mb-2 text-orange-200">
              Classement provisoire indisponible
            </h3>
            <p className="text-sm text-white/60 max-w-md mx-auto">
              Le classement provisoire est visible uniquement après ta
              participation. Joue le quiz pour le débloquer.
            </p>
            <button
              onClick={() => window.location.reload()}
              data-testid="leaderboard-pending-refresh"
              className="mt-5 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-display uppercase tracking-wider"
            >
              Actualiser
            </button>
          </div>
        ) : data.rows.length === 0 ? (
          <div className="panel rounded-2xl p-10 text-center text-white/50">
            Aucun joueur n&apos;a participé à ce défi.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 items-end mb-10">
              {order.map((rank, slot) => {
                const p = top3[rank];
                if (!p) return <div key={slot} className="h-32" />;
                const heights = ["h-44", "h-60", "h-36"];
                const colors = [
                  "from-cyan-400 to-cyan-600 border-cyan-300",
                  "from-amber-300 to-amber-500 border-amber-200",
                  "from-fuchsia-500 to-fuchsia-700 border-fuchsia-300",
                ];
                const labels = ["2ème", "1er", "3ème"];
                const icons = [
                  <Medal key="m" className="w-7 h-7" />,
                  <Crown key="c" className="w-9 h-9" />,
                  <Medal key="m2" className="w-7 h-7" />,
                ];
                return (
                  <div
                    key={slot}
                    className="flex flex-col items-center"
                    data-testid={`podium-slot-${slot}`}
                  >
                    <div className="mb-2 font-display font-bold truncate max-w-[120px] text-center">
                      {p.nickname}
                    </div>
                    <div className="text-cyan-300 font-mono-stat font-bold mb-2">
                      {p.total_score} pts
                    </div>
                    <div
                      className={`w-full ${heights[slot]} rounded-t-2xl bg-gradient-to-b ${colors[slot]} border flex flex-col items-center justify-start pt-3 text-black font-display font-black`}
                    >
                      {icons[slot]}
                      <div className="mt-1 text-base uppercase tracking-widest">
                        {labels[slot]}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {(others.length > 0 || isProvisional || isQualifier || hasCashPool) && (
              <div className="panel rounded-2xl p-6">
                <div className="text-xs uppercase tracking-widest font-mono-stat text-white/40 mb-3">
                  Classement {isProvisional ? "provisoire" : "final"}
                </div>
                <ul className="space-y-2">
                  {data.rows.map((p, i) => {
                    const rank = i + 1;
                    // v2.46 — provisional / final status pills.
                    let statusChip = null;
                    if (isQualifier && qualSlots > 0) {
                      const qualified = p.provisional_qualified || rank <= qualSlots;
                      statusChip = qualified ? (
                        <span
                          data-testid={`leaderboard-row-${i}-status`}
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-display font-bold bg-cyan-400/15 border border-cyan-400/50 text-cyan-200"
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          {isProvisional ? "Qualifié provisoire" : "Qualifié"}
                        </span>
                      ) : (
                        <span
                          data-testid={`leaderboard-row-${i}-status`}
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-display font-bold bg-white/5 border border-white/15 text-white/50"
                        >
                          Hors qualification
                        </span>
                      );
                    } else if (hasCashPool && winnersCount > 0) {
                      const prize = Number(p.provisional_prize || 0);
                      const inMoney = prize > 0 || rank <= winnersCount;
                      statusChip = inMoney ? (
                        <span
                          data-testid={`leaderboard-row-${i}-status`}
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-display font-bold bg-amber-400/15 border border-amber-400/50 text-amber-200"
                        >
                          <Trophy className="w-2.5 h-2.5" />
                          {isProvisional ? "Gain provisoire" : "Gain"} · {formatEuros(prize)}
                        </span>
                      ) : (
                        <span
                          data-testid={`leaderboard-row-${i}-status`}
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-display font-bold bg-white/5 border border-white/15 text-white/50"
                        >
                          Hors gain
                        </span>
                      );
                    }
                    return (
                      <li
                        key={`${p.nickname}-${i}`}
                        data-testid={`leaderboard-row-${i}`}
                        className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${
                          p.is_me
                            ? "bg-cyan-400/10 border-cyan-400/50"
                            : "bg-white/5 border-white/10"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono-stat font-bold text-white/50 w-8 shrink-0">
                            #{rank}
                          </span>
                          <span className="font-display font-bold truncate">
                            {p.nickname}
                            {p.is_me && (
                              <span className="ml-1 text-cyan-300 text-xs font-mono-stat">
                                (toi)
                              </span>
                            )}
                          </span>
                          {statusChip}
                        </div>
                        <span className="font-mono-stat font-bold text-cyan-300 shrink-0">
                          {p.total_score}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
